import React, { useState, useMemo } from 'react';
import { Button, Row, Table } from 'antd';

import { useGetPaginatedPromotions } from 'utils/apis/rateModifier';
import { generateDisplayPercentage, generateDisplayFee, getConstantLabel, guard } from 'utils/general';
import { constructColumn, constructColumnFilterSearch, handleOnAggregationTableChange } from 'utils/table/table';

import { useFetchConstant } from 'hooks/constants';

import ModalPromotion from '../components/ModalPromotion/ModalPromotion';

import styles from '../RateModifier.module.css';
import intl from 'react-intl-universal';

const constructColumns = (rateModifierTypes, promotionStatuses, handleOnClickEditPromotion) => {
  const columns = [
    {
      ...constructColumn(intl.get('rateModifier.tableColumns.promotion').d('Promotion Name'), 'name', { hasSorter: true }),
      ...constructColumnFilterSearch('name', intl.get('tables.promotionName').d('Search Promotion Name')),
      render: (text, record) => {
        return (
          <Button type="link" onClick={() => handleOnClickEditPromotion(record)}>
            {text}
          </Button>
        );
      }
    },
    {
      ...constructColumn(intl.get('rateModifier.tableColumns.host').d('Host Name'), 'hostName', { hasSorter: true }),
      ...constructColumnFilterSearch('hostName', intl.get('tables.hostName').d('Search Host'))
    },
    {
      ...constructColumn(intl.get('rateModifier.tableColumns.amount').d('Amount/Percentage'), 'amount', { hasSorter: true }),
      render: (text, record) => {
        const rateModifierTypeObject = rateModifierTypes.find(type => type.code === record.type);
        return guard(
          () => (rateModifierTypeObject.isPercentage ? generateDisplayPercentage(text, true) : generateDisplayFee(text, true, 0, record.currency)),
          ''
        );
      }
    },
    {
      ...constructColumn(intl.get('rateModifier.tableColumns.promotionStart').d('Promotion Start'), 'promotionDetails.promotionPeriod.startDate', {
        hasSorter: true
      })
    },
    {
      ...constructColumn(intl.get('rateModifier.tableColumns.promotionEnd').d('Promotion End'), 'promotionDetails.promotionPeriod.endDate', {
        hasSorter: true
      })
    },
    {
      ...constructColumn(intl.get('rateModifier.tableColumns.status').d('Status'), 'promotionDetails.status', { hasSorter: true }),
      render: status => {
        return intl.get(`rateModifier.status.${getConstantLabel(promotionStatuses, status)}`).d('status');
      }
    }
  ];

  return columns;
};

const useFetchConstants = () => {
  const { selection: rateModifierTypes, isLoading: isRateModifierTypesLoading } = useFetchConstant('rateModifierType');
  const { selection: rateModifierSigns, isLoading: isRateModifierSignsLoading } = useFetchConstant('rateModifierSign');
  const { selection: promotionStatuses, isLoading: isPromotionStatusesLoading } = useFetchConstant('promotionStatus');

  const selections = { rateModifierTypes, rateModifierSigns, promotionStatuses };
  const isLoading = isRateModifierTypesLoading || isRateModifierSignsLoading || isPromotionStatusesLoading;

  return { selections, isLoading };
};

const Promotion = ({ isAllowCreateRateModifier, isAllowEditRateModifier, isAllowDeleteRateModifier, checkIsAdminReadOnly }) => {
  const [query, setQuery] = useState({});
  const [isShowModalPromotion, setIsShowModalPromotion] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState('');

  const {
    isLoading: isConstantsLoading,
    selections: { rateModifierTypes, rateModifierSigns, promotionStatuses }
  } = useFetchConstants();
  const { paginatedData: promotions, total, isLoading: isPromotionsLoading, refetch: refetchPromotions } = useGetPaginatedPromotions(query);

  const filteredRateModifierSigns = useMemo(() => !!rateModifierSigns && rateModifierSigns.filter(sign => sign.isPositive === false), [
    rateModifierSigns
  ]);

  const isLoading = isConstantsLoading || isPromotionsLoading;

  const handleOnClickEditPromotion = promotion => {
    setIsShowModalPromotion(true);
    setSelectedPromotion(promotion);
  };

  const handleOnModalPromotionClose = () => {
    setIsShowModalPromotion(false);
    setSelectedPromotion('');
    refetchPromotions();
  };

  return (
    <>
      {isShowModalPromotion && (
        <ModalPromotion
          isShowModalPromotion={isShowModalPromotion}
          selectedPromotion={selectedPromotion}
          onModalPromotionClose={handleOnModalPromotionClose}
          rateModifierTypes={rateModifierTypes}
          rateModifierSigns={filteredRateModifierSigns}
          isAllowEditRateModifier={isAllowEditRateModifier}
          isAllowDeleteRateModifier={isAllowDeleteRateModifier}
          checkIsAdminReadOnly={checkIsAdminReadOnly}
        />
      )}
      {isAllowCreateRateModifier && (
        <Row>
          <Button id="create-button3-rate" type="primary" icon="plus" onClick={() => setIsShowModalPromotion(true)} disabled={checkIsAdminReadOnly()}>
            {intl.get('rateModifier.headerLabels.createPromotion').d('Create Promotion')}
          </Button>
        </Row>
      )}
      <Row className={styles.table}>
        <Table
          loading={isLoading}
          rowKey={record => record._id}
          dataSource={promotions}
          columns={constructColumns(rateModifierTypes, promotionStatuses, handleOnClickEditPromotion)}
          pagination={{ total }}
          onChange={(pagination, filters, sorter) => handleOnAggregationTableChange({ pagination, filters, sorter }, setQuery)}
        />
      </Row>
    </>
  );
};

export default Promotion;
